import React, { useMemo, useRef } from 'react';
import useEllieMarker from './useEllieMarker';

/**
 * Club session-distribution curve with a scroll-tracked "Ellie" marker — a verbatim
 * transcription of the mockup's SVG (source: mockup L487–509). The dynamic elements are
 * held by refs and driven by {@link useEllieMarker}.
 *
 * Fonts are inline styles, not SVG presentation attributes: Chakra's global
 * reset (`* { font: inherit }`) is author CSS, which outranks presentation
 * attributes and would silently replace Bebas/Roboto with the body font.
 */
const CHART_FONTS = {
  ellie: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '16px' },
  pill: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '12.5px' },
  axis: { fontFamily: 'Roboto, sans-serif', fontSize: '12px' },
  caption: { fontFamily: 'Roboto, sans-serif', fontSize: '11.5px' },
} satisfies Record<string, React.CSSProperties>;

const DistributionChart = (): React.JSX.Element => {
  const svg = useRef<SVGSVGElement>(null);
  const curve = useRef<SVGPathElement>(null);
  const line = useRef<SVGLineElement>(null);
  const dot = useRef<SVGCircleElement>(null);
  const label = useRef<SVGTextElement>(null);
  const pillRect = useRef<SVGRectElement>(null);
  const pillDot = useRef<SVGCircleElement>(null);
  const pillText = useRef<SVGTextElement>(null);

  const refs = useMemo(
    () => ({ svg, curve, line, dot, label, pillRect, pillDot, pillText }),
    [],
  );
  useEllieMarker(refs);

  return (
    <div className="oq-engagement__chart">
      <svg
        ref={svg}
        viewBox="0 0 560 210"
        role="img"
        aria-label="Distribution of sessions attended per member across the club, with one member's position marked"
        className="oq-engagement__chart-svg"
      >
        <defs>
          <linearGradient id="distf" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#2D8CBA" stopOpacity="0.20" />
            <stop offset="1" stopColor="#2D8CBA" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path
          d="M40,118 C60,96 76,60 94,58 C120,56 138,78 164,98 C214,138 300,152 384,158 C444,162 492,163 520,163 L520,170 L40,170 Z"
          fill="url(#distf)"
        />
        <path
          ref={curve}
          id="dist-curve"
          d="M40,118 C60,96 76,60 94,58 C120,56 138,78 164,98 C214,138 300,152 384,158 C444,162 492,163 520,163"
          fill="none"
          stroke="#7FBFD8"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <line x1="40" y1="170" x2="520" y2="170" stroke="#E3E3E8" strokeWidth="1.5" />
        <line
          ref={line}
          x1="72"
          y1="170"
          x2="72"
          y2="34"
          stroke="#19192D"
          strokeWidth="1.5"
          strokeDasharray="3 4"
        />
        <circle ref={dot} cx="72" cy="82" r="6" fill="#2D8CBA" stroke="#ffffff" strokeWidth="3" />
        <text
          ref={label}
          x="40"
          y="24"
          textAnchor="start"
          style={CHART_FONTS.ellie}
          letterSpacing="0.5"
          fill="#19192D"
        >
          Ellie · 7
        </text>
        <rect ref={pillRect} x="96" y="8" width="92" height="22" rx="11" fill="#FEE2E2" />
        <circle ref={pillDot} cx="110" cy="19" r="4.5" fill="#DC2626" />
        <text
          ref={pillText}
          x="122"
          y="23"
          textAnchor="start"
          style={CHART_FONTS.pill}
          letterSpacing="0.6"
          fill="#B91C1C"
        >
          UNENGAGED
        </text>
        <g style={CHART_FONTS.axis} fill="#b0b0ba" textAnchor="middle">
          <text x="40" y="192">
            0
          </text>
          <text x="280" y="192">
            56
          </text>
          <text x="520" y="192">
            112
          </text>
        </g>
        <text
          x="280"
          y="206"
          textAnchor="middle"
          style={CHART_FONTS.caption}
          fill="#9A9AA4"
        >
          sessions attended per member
        </text>
      </svg>
    </div>
  );
};

export default DistributionChart;
